<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { endpoints, type PendingInvitationResponse } from '@/api'
import { ApiError } from '@/api/client-exports'
import { useOrganizerAuthStore } from '@/stores/organizerAuth'

/**
 * Accepting a co-organizer invitation (SPEC §10.2.1).
 *
 * Written for somebody who may never have seen this product. It says who asked
 * and what for before it asks for anything, and the shape of what it asks
 * depends on whether the invited address already has an account.
 *
 * The token is not on this page. The emailed link goes to the API, which puts
 * it in an HttpOnly cookie and redirects here with only a status.
 */

const route = useRoute()
const router = useRouter()
const auth = useOrganizerAuthStore()

const invitation = ref<PendingInvitationResponse | null>(null)
const loading = ref(true)
const busy = ref(false)
const done = ref(false)
const error = ref<string | null>(null)
const fieldErrors = ref<Record<string, string[]>>({})

const displayName = ref('')
const password = ref('')

const linkUsable = computed(() => route.query.status === 'ready')

/** Signed in as the person the invitation was for. */
const signedInAsInvitee = computed(
  () => auth.isSignedIn && auth.email?.toLowerCase() === invitation.value?.email.toLowerCase(),
)

onMounted(async () => {
  if (!linkUsable.value) {
    loading.value = false
    return
  }

  await auth.ensureLoaded()

  try {
    invitation.value = await endpoints.pendingInvitation()
  } catch {
    // Any failure here means there is no usable invitation, and the page says
    // so rather than offering a form that cannot work.
    invitation.value = null
  } finally {
    loading.value = false
  }
})

function errorFor(field: string): string | undefined {
  const key = Object.keys(fieldErrors.value).find((k) => k.toLowerCase() === field.toLowerCase())
  return key ? fieldErrors.value[key]?.[0] : undefined
}

async function accept(): Promise<void> {
  await run(() => endpoints.acceptInvitation())
}

async function registerAndAccept(): Promise<void> {
  fieldErrors.value = {}

  if (!displayName.value.trim()) {
    fieldErrors.value = { displayName: ['Tell us what to call you.'] }
    return
  }

  await run(() =>
    endpoints.registerFromInvitation({
      displayName: displayName.value.trim(),
      password: password.value,
    }),
  )
}

async function run(action: () => Promise<unknown>): Promise<void> {
  error.value = null
  busy.value = true

  try {
    await action()
    done.value = true

    // The session changed, so what the application believes about the signed-in
    // organizer has to be re-established rather than patched.
    auth.clear()
    await auth.ensureLoaded()
  } catch (cause) {
    if (cause instanceof ApiError && cause.problem?.errors) {
      fieldErrors.value = cause.problem.errors
    } else {
      error.value =
        cause instanceof ApiError
          ? (cause.problem?.detail ?? cause.userMessage)
          : 'Something went wrong. Try again.'
    }
  } finally {
    busy.value = false
  }
}

function goToDashboard(): void {
  void router.push({ name: 'organizer-dashboard' })
}
</script>

<template>
  <v-container class="py-10" style="max-width: 32rem">
    <div v-if="loading" class="py-8 text-body-2" style="opacity: 0.75" role="status">Loading…</div>

    <div v-else-if="done" data-testid="invitation-accepted">
      <h1 class="text-h5 mb-3">You are in</h1>
      <p class="text-body-1 mb-6">You can now help organize {{ invitation?.groupName }}.</p>
      <v-btn color="primary" @click="goToDashboard">Go to the dashboard</v-btn>
    </div>

    <div v-else-if="!invitation" data-testid="invitation-closed">
      <h1 class="text-h5 mb-3">That invitation is no longer open</h1>
      <p class="text-body-1 mb-6">
        Invitations last seven days and can be withdrawn. Ask whoever invited you to send another.
      </p>
      <v-btn variant="text" :to="{ name: 'landing' }">Back to the start</v-btn>
    </div>

    <template v-else>
      <h1 class="text-h5 mb-2">Help organize {{ invitation.groupName }}</h1>
      <p class="text-body-1 mb-6">
        {{ invitation.inviterName }} has asked you to help with the meals, who is coming, and who is
        bringing what.
      </p>

      <div
        v-if="error"
        data-testid="invitation-error"
        role="alert"
        class="mb-4 pa-3 rounded text-body-2"
        style="background-color: rgb(var(--v-theme-error)); color: rgb(var(--v-theme-on-error))"
      >
        {{ error }}
      </div>

      <!-- Already signed in as the right person: one button, nothing to type. -->
      <template v-if="signedInAsInvitee">
        <v-btn color="primary" size="large" block :loading="busy" :disabled="busy" @click="accept">
          Accept the invitation
        </v-btn>
      </template>

      <!-- Has an account but is not signed in, or is signed in as somebody
           else. Either way the answer is to sign in as the invited address. -->
      <template v-else-if="invitation.hasAccount">
        <p class="text-body-2 mb-4" style="opacity: 0.75">
          This invitation was sent to {{ invitation.email }}. Sign in as that person to accept it.
        </p>
        <v-btn
          color="primary"
          size="large"
          block
          :to="{
            name: 'organizer-sign-in',
            query: { returnTo: '/admin/invitations?status=ready' },
          }"
        >
          Sign in
        </v-btn>
      </template>

      <!-- No account yet: set one up here. -->
      <template v-else>
        <p class="text-body-2 mb-4" style="opacity: 0.75">
          Choose a password and you are done — we already know your email is
          {{ invitation.email }}.
        </p>

        <v-form novalidate @submit.prevent="registerAndAccept">
          <v-text-field
            v-model="displayName"
            label="Your name"
            name="displayName"
            autocomplete="name"
            :error-messages="errorFor('displayName')"
            :disabled="busy"
            class="mb-2"
          />

          <v-text-field
            v-model="password"
            label="Password"
            name="password"
            type="password"
            autocomplete="new-password"
            hint="At least 12 characters. A few ordinary words works well."
            persistent-hint
            :error-messages="errorFor('password')"
            :disabled="busy"
            class="mb-6"
          />

          <v-btn type="submit" color="primary" size="large" block :loading="busy" :disabled="busy">
            Join and start helping
          </v-btn>
        </v-form>
      </template>
    </template>
  </v-container>
</template>
